import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDevelopmentRequestRequest,
  DevelopmentRequestResponse,
  UpdateDevelopmentRequestRequest,
} from "../types/developmentRequest";
import type { PaginatedList } from "../types/paginated";
import { apiClient } from "./client";

export interface ListRequestsParams {
  status?: string;
  audience?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  page?: number;
  limit?: number;
  mine?: boolean;
}

export async function getRequests(params?: ListRequestsParams): Promise<PaginatedList<DevelopmentRequestResponse>> {
  const res = await apiClient.get<PaginatedList<DevelopmentRequestResponse>>("/requests", { params });
  return res.data;
}

export async function getRequest(id: number): Promise<DevelopmentRequestResponse> {
  const res = await apiClient.get<DevelopmentRequestResponse>(`/requests/${id}`);
  return res.data;
}

export async function createRequest(data: CreateDevelopmentRequestRequest): Promise<DevelopmentRequestResponse> {
  const res = await apiClient.post<DevelopmentRequestResponse>("/requests", data);
  return res.data;
}

export async function updateRequest(
  id: number,
  data: UpdateDevelopmentRequestRequest,
): Promise<DevelopmentRequestResponse> {
  const res = await apiClient.patch<DevelopmentRequestResponse>(`/requests/${id}`, data);
  return res.data;
}

export async function approveRequest(id: number): Promise<DevelopmentRequestResponse> {
  const res = await apiClient.post<DevelopmentRequestResponse>(`/requests/${id}/approve`);
  return res.data;
}

export async function deleteRequest(id: number): Promise<void> {
  await apiClient.delete(`/requests/${id}`);
}

export function useRequests(params?: ListRequestsParams) {
  return useQuery<PaginatedList<DevelopmentRequestResponse>>({
    queryKey: ["requests", params],
    queryFn: () => getRequests(params),
  });
}

export function useRequest(id: number) {
  return useQuery({
    queryKey: ["requests", id],
    queryFn: () => getRequest(id),
    enabled: !!id,
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createRequest,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}

export function useUpdateRequest(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateDevelopmentRequestRequest) => updateRequest(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests"] });
      void qc.invalidateQueries({ queryKey: ["requests", id] });
    },
  });
}

export function useApproveRequest(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => approveRequest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests"] });
      void qc.invalidateQueries({ queryKey: ["requests", id] });
    },
  });
}

export function useDeleteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteRequest(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests"] });
    },
  });
}
